import moment, { Moment } from 'moment-timezone';

import { GeneralModel, StatisticsModel } from '../modules/models';

export const getStartDateAndEndDateFromPeriodFilter = (selectedFilter: number) => {
  let dates = {
    startDate: null,
    endDate: null,
  };
  switch (selectedFilter) {
    case GeneralModel.DashboardPeriodFilterType.CURRENT_DAY:
      dates.startDate = moment()
        .startOf('day')
        .format('YYYY-MM-DD');
      break;
    case GeneralModel.DashboardPeriodFilterType.PREVIOUS_DAY:
      dates.startDate = moment()
        .startOf('day')
        .subtract(1, 'days')
        .format('YYYY-MM-DD');
      dates.endDate = moment()
        .startOf('day')
        .subtract(1, 'days')
        .format('YYYY-MM-DD');
      break;
    case GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS:
      dates.startDate = moment()
        .startOf('day')
        .subtract(7, 'days')
        .format('YYYY-MM-DD');
      break;
    case GeneralModel.DashboardPeriodFilterType.ALL_TIMES:
    case GeneralModel.DashboardPeriodFilterType.DATE_RANGE:
      break;
  }
  return dates;
};

export const isDateBetweenProjectDates = (
  projectStartDate: string | Moment,
  projectEndDate: string | Moment,
  projectCreationDate: string | Moment,
  testDate: string | Moment
) => {
  const projectEndDateMomentWithoutTime = projectEndDate ? moment(projectEndDate).startOf('day') : null;
  const projectStartDateMomentWithoutTime = projectStartDate ? moment(projectStartDate).startOf('day') : null;
  const projectCreationDateMomentWithoutTime = projectCreationDate ? moment(projectCreationDate).startOf('day') : null;
  const testDateMomentWithoutTime = moment(testDate).startOf('day');

  if (projectEndDate) {
    if (projectEndDateMomentWithoutTime.isSameOrAfter(testDateMomentWithoutTime)) {
      if (projectStartDate && projectCreationDate) {
        if (moment.min(projectStartDateMomentWithoutTime, moment(projectCreationDateMomentWithoutTime)).isSameOrBefore(testDateMomentWithoutTime)) {
          return true;
        } else {
          return false;
        }
      } else if (projectStartDate || projectCreationDate) {
        if ((projectStartDateMomentWithoutTime || projectCreationDateMomentWithoutTime).isSameOrBefore(testDateMomentWithoutTime)) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
  } else {
    if (projectStartDate && projectCreationDate) {
      if (moment.min(projectStartDateMomentWithoutTime, moment(projectCreationDateMomentWithoutTime)).isSameOrBefore(testDateMomentWithoutTime)) {
        return true;
      } else {
        return false;
      }
    } else if (projectStartDate || projectCreationDate) {
      if ((projectStartDateMomentWithoutTime || projectCreationDateMomentWithoutTime).isSameOrBefore(testDateMomentWithoutTime)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }
  return false;
};

export const getMinAndMaxDatesFromFirstLevelFilters = (projectStartDate: string, projectEndDate: string, projectCreationDate: string) => {
  let minDate = null;

  if (projectStartDate && projectCreationDate) {
    minDate = moment.min(moment(projectStartDate), moment(projectCreationDate)).format('YYYY-MM-DD');
  } else {
    minDate = projectStartDate || projectCreationDate;
  }

  return {
    minDate: minDate,
    maxDate: projectEndDate,
  };
};

export const getValidPeriodFilters = (projectStartDate: string, projectEndDate: string, projectCreationDate: string) => {
  let validPeriodFilterValues = [];
  const today = moment();
  const yesterday = moment().subtract(1, 'days');
  const dayBeforeSevenDays = moment().subtract(7, 'days');

  // Validating GeneralModel.DashboardPeriodFilterType.ALL_TIMES
  if (!projectStartDate && !projectCreationDate && !projectEndDate) {
    validPeriodFilterValues.push(GeneralModel.DashboardPeriodFilterType.ALL_TIMES);
  }

  // Validating GeneralModel.DashboardPeriodFilterType.CURRENT_DAY
  if (isDateBetweenProjectDates(projectStartDate, projectEndDate, projectCreationDate, today)) {
    validPeriodFilterValues.push(GeneralModel.DashboardPeriodFilterType.CURRENT_DAY);
  }

  // Validating GeneralModel.DashboardPeriodFilterType.PREVIOUS_DAY
  if (isDateBetweenProjectDates(projectStartDate, projectEndDate, projectCreationDate, yesterday)) {
    validPeriodFilterValues.push(GeneralModel.DashboardPeriodFilterType.PREVIOUS_DAY);
  }

  // Validating GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS
  if (
    isDateBetweenProjectDates(projectStartDate, projectEndDate, projectCreationDate, dayBeforeSevenDays) &&
    validPeriodFilterValues.includes(GeneralModel.DashboardPeriodFilterType.CURRENT_DAY)
  ) {
    validPeriodFilterValues.push(GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS);
  }

  // Date Range is always valid so pushing it
  validPeriodFilterValues.push(GeneralModel.DashboardPeriodFilterType.DATE_RANGE);

  return validPeriodFilterValues;
};

export const getValidPeriodFilterOptionList = (projectStartDate: string, projectEndDate: string, projectCreationDate: string) => {
  const validPeriodFilterValues = getValidPeriodFilters(projectStartDate, projectEndDate, projectCreationDate);
  return GeneralModel.DashboardPeriodFilterOptionList.filter(option => validPeriodFilterValues.includes(option.value));
};

export const getValidPeriodFilterDataFromProjectDates = (
  projectStartDate: string,
  projectEndDate: string,
  projectCreationDate: string,
  periodFilter: number,
  startDate: string,
  endDate: string
) => {
  let validPeriodFilterValues = getValidPeriodFilters(projectStartDate, projectEndDate, projectCreationDate);

  if (validPeriodFilterValues.includes(periodFilter)) {
    if (periodFilter === GeneralModel.DashboardPeriodFilterType.DATE_RANGE) {
      let newStartDate = null;
      let newEndDate = null;
      // Validating start date and end date
      if (isDateBetweenProjectDates(projectStartDate, projectEndDate, projectCreationDate, startDate)) {
        newStartDate = startDate;
      }

      if (isDateBetweenProjectDates(projectStartDate, projectEndDate, projectCreationDate, endDate)) {
        newEndDate = endDate;
      }

      return {
        startDate: newStartDate,
        endDate: newEndDate,
        periodFilter: periodFilter,
      };
    } else {
      return {
        startDate: startDate,
        endDate: endDate,
        periodFilter: periodFilter,
      };
    }
  } else {
    return {
      periodFilter: validPeriodFilterValues?.includes(GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS)
        ? GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS
        : validPeriodFilterValues[0],
      ...getStartDateAndEndDateFromPeriodFilter(validPeriodFilterValues[0]),
    };
  }
};

export const sanitizeDashboardApiQueryParams = (
  {
    queryParams,
    hasDates,
    hasTimezone,
    hasPeriodFilter,
  }: { queryParams: GeneralModel.IQueryParams; hasDates?: boolean; hasTimezone?: boolean; hasPeriodFilter?: boolean } = { queryParams: {} }
) => {
  const params: GeneralModel.IQueryParams = {
    selectedCompanyIds: queryParams.selectedCompanyIds,
    selectedProjectIds: queryParams.selectedProjectIds,
  };

  if (hasTimezone) {
    // params.timeZoneOffset = queryParams.timeZoneOffset;
  }

  if (hasDates) {
    if (queryParams.startDate && !queryParams.startDate?.includes('T')) {
      params.startDate = queryParams.startDate + 'T00:00:00';
    } else {
      params.startDate = queryParams.startDate;
    }

    if (queryParams.endDate && !queryParams.endDate?.includes('T')) {
      params.endDate = queryParams.endDate + 'T23:59:59';
    } else {
      params.endDate = queryParams.endDate;
    }
  }

  if (hasPeriodFilter) {
    params.periodFilter = queryParams.periodFilter;
  }

  return params;
};

export const filterNameMapper = {
  companyList: 'companyList',
  projectList: 'projectList',
  countries: 'countries',
  states: 'states',
  periodFilter: 'periodFilter',
  startDate: 'startDate',
  endDate: 'endDate',
};

export const filterLabelMapper = {
  [filterNameMapper.companyList]: 'Company',
  [filterNameMapper.projectList]: 'Project',
  [filterNameMapper.countries]: 'Country',
  [filterNameMapper.states]: 'State or Province',
  [filterNameMapper.periodFilter]: 'Activity Date Range',
  [filterNameMapper.startDate]: 'Activity Start Date',
  [filterNameMapper.endDate]: 'Activity End Date',
};

export const exportFileFormatKeys = {
  pdf: 'pdf',
  excel: 'excel',
};

export const exportFileFormatLabels = {
  [exportFileFormatKeys.pdf]: 'PDF',
  [exportFileFormatKeys.excel]: 'Excel',
};

export const exportFileFormatValues = {
  [exportFileFormatKeys.pdf]: 'pdf',
  [exportFileFormatKeys.excel]: 'excel',
};

export const exportFileFormatOptions = [
  { label: exportFileFormatLabels[exportFileFormatKeys.pdf], value: exportFileFormatValues[exportFileFormatKeys.pdf] },
  { label: exportFileFormatLabels[exportFileFormatKeys.excel], value: exportFileFormatValues[exportFileFormatKeys.excel] },
];

export const exportFileFormatLabelMapper = {
  [StatisticsModel.ExportFormatType.PDF]: 'PDF',
  [StatisticsModel.ExportFormatType.EXCEL]: 'Excel',
};

export const expandedWidgets = {
  wfmWorkersOnsite: 'wfmWorkersOnsite',
  wfmCompaniesOnsite: 'wfmCompaniesOnsite',
  wfmTradesOnsite: 'wfmTradesOnsite',
  dailyLogNewWorkersBadged: 'dailyLogNewWorkersBadged',
  dailyLogNewCompaniesEnrolled: 'dailyLogNewCompaniesEnrolled',
  dailyLogTotalTrades: 'dailyLogTotalTrades',
  dailyLogTotalHeadcount: 'dailyLogTotalHeadcount',
  dailyLogTotalCompanies: 'dailyLogTotalCompanies',
  dailyLogTotalHours: 'dailyLogTotalHours',
};

export const expandedWidgetsExportLoaderMapper = {
  [expandedWidgets.wfmWorkersOnsite]: 'EXPORT_WIDGET_DATA_WFM_WORKERS_ONSITE',
  [expandedWidgets.wfmCompaniesOnsite]: 'EXPORT_WIDGET_DATA_WFM_COMPANIES_ONSITE',
  [expandedWidgets.wfmTradesOnsite]: 'EXPORT_WIDGET_DATA_WFM_TRADES_ONSITE',
  [expandedWidgets.dailyLogNewWorkersBadged]: 'EXPORT_WIDGET_DATA_DAILY_LOG_NEW_WORKERS_BADGED',
  [expandedWidgets.dailyLogNewCompaniesEnrolled]: 'EXPORT_WIDGET_DATA_DAILY_LOG_NEW_COMPANIES_ENROLLED',
  [expandedWidgets.dailyLogTotalTrades]: 'EXPORT_WIDGET_DATA_DAILY_LOG_TOTAL_TRADES',
  [expandedWidgets.dailyLogTotalHeadcount]: 'EXPORT_WIDGET_DATA_DAILY_LOG_TOTAL_HEADCOUNT',
  [expandedWidgets.dailyLogTotalCompanies]: 'EXPORT_WIDGET_DATA_DAILY_LOG_TOTAL_COMPANIES',
  [expandedWidgets.dailyLogTotalHours]: 'EXPORT_WIDGET_DATA_DAILY_LOG_TOTAL_HOURS',
};

export const generateDateStringFromActivityDateRange = (periodFilter: any, startDate: string, endDate: string, initialWord?: string) => {
  let dateString = `${initialWord ? initialWord + ' ' : ''}`;
  switch (periodFilter) {
    case GeneralModel.DashboardPeriodFilterType.ALL_TIMES: {
      dateString = dateString + 'in total';
      break;
    }
    case GeneralModel.DashboardPeriodFilterType.CURRENT_DAY: {
      dateString = dateString + 'today';
      break;
    }
    case GeneralModel.DashboardPeriodFilterType.PREVIOUS_DAY: {
      dateString = dateString + 'yesterday';
      break;
    }
    case GeneralModel.DashboardPeriodFilterType.LAST_SEVEN_DAYS: {
      dateString = dateString + 'over the last 7 days';
      break;
    }
    case GeneralModel.DashboardPeriodFilterType.DATE_RANGE: {
      if (startDate && endDate) {
        dateString = `${dateString}from ${moment(startDate).format('MM/DD/YYYY')} to ${moment(endDate).format('MM/DD/YYYY')}`;
      } else if (startDate) {
        dateString = `${dateString}from ${moment(startDate).format('MM/DD/YYYY')}`;
      } else if (endDate) {
        dateString = `${dateString}until ${moment(endDate).format('MM/DD/YYYY')}`;
      } else {
        dateString = '';
      }
      break;
    }
    default:
      return dateString;
  }
  return dateString;
};

export const getRemainingDownloadTime = (startTime: string, estimatedTime: string): number => {
  let remainingTime = 0;
  let splittedEstimatedTime = estimatedTime.split(':');
  let estimatedSeconds = Number(splittedEstimatedTime[0]) * 3600 + Number(splittedEstimatedTime[1]) * 60 + Number(splittedEstimatedTime[2]);
  let estimatedDownloadTime = moment.utc(startTime).add(estimatedSeconds, 'seconds');

  remainingTime = estimatedDownloadTime.diff(moment(), 'seconds');
  return remainingTime;
};
